import { useMemo, useState } from 'react';

import { CATEGORY_LABELS, CATEGORY_ORDER, type Category, type Program } from '../core/types';
import { formatDuration, totalSessionMinutes, validateProgram } from '../core/validator';
import { describeFrequency, describeTiming } from '../data/programs';
import { useConnection } from '../store/connection';
import { useStore } from '../store/store';
import { Modal } from './common';
import { useConfirm } from './confirm';

export function Library({ onEdit }: { onEdit: () => void }) {
  const store = useStore();
  const conn = useConnection();
  const confirm = useConfirm();
  const [query, setQuery] = useState('');
  const [notConnected, setNotConnected] = useState(false);
  const [running, setRunning] = useState<string | null>(null);

  const connected = conn.state === 'connected';

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return store.programs;
    return store.programs.filter((p) =>
      [p.name, p.title ?? '', p.info?.purpose ?? '', p.info?.origin ?? '', p.notes ?? '']
        .join(' ')
        .toLowerCase()
        .includes(q),
    );
  }, [store.programs, query]);

  const favorites = matches.filter((p) => p.favorite);

  const grouped = useMemo(() => {
    const map = new Map<Category | 'custom', Program[]>();
    for (const p of matches) {
      const key = p.category ?? 'custom';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(p);
    }
    return map;
  }, [matches]);

  const play = async (program: Program) => {
    if (!connected) {
      setNotConnected(true);
      return;
    }
    const result = validateProgram(program, store.profile.avoidBands);
    if (!result.canRun) {
      await confirm({
        title: 'This program needs a change first',
        body: result.errors[0]?.message ?? 'Something in this program is out of range.',
        confirmLabel: 'OK',
      });
      return;
    }
    setRunning(program.id);
    try {
      await conn.sendProgram(program, { run: true });
      store.logSession(program, result.totalMin);
    } finally {
      setRunning(null);
    }
  };

  const card = (program: Program) => {
    const total = totalSessionMinutes(program.cycles);
    const busy = running === program.id;

    return (
      <div className="card" key={program.id}>
        <div className="row" style={{ alignItems: 'flex-start' }}>
          <button
            className={`play-btn ${connected ? '' : 'idle'}`}
            aria-label={`Play ${program.title ?? program.name}`}
            disabled={busy}
            onClick={() => void play(program)}
          >
            {busy ? '…' : '▶'}
          </button>

          <div className="grow">
            <h3 className="card-title">{program.title ?? program.name}</h3>
            <div className="row wrap muted" style={{ gap: 6, marginTop: 2 }}>
              <span className="mono">{describeFrequency(program.cycles)}</span>
              <span aria-hidden="true">·</span>
              <span className="mono">{describeTiming(program.cycles)}</span>
              {program.cycles.length > 1 ? (
                <>
                  <span aria-hidden="true">·</span>
                  <span className="mono">{formatDuration(total)} total</span>
                </>
              ) : null}
            </div>
          </div>

          <button
            className={`star-btn ${program.favorite ? 'on' : ''}`}
            aria-label={program.favorite ? 'Remove from favourites' : 'Add to favourites'}
            aria-pressed={!!program.favorite}
            onClick={() => store.toggleFavorite(program.id)}
          >
            {program.favorite ? '♥' : '♡'}
          </button>

          <button
            className="icon-btn"
            aria-label={`Edit ${program.title ?? program.name}`}
            onClick={() => {
              store.select(program.id);
              onEdit();
            }}
          >
            ✎
          </button>
        </div>

        {program.info?.purpose ? (
          <p className="muted" style={{ margin: '10px 0 0' }}>
            {program.info.purpose}
          </p>
        ) : null}
      </div>
    );
  };

  return (
    <>
      <div className="card">
        <div className="row">
          <input
            type="text"
            placeholder="Search — sleep, allergy, liver…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search programs"
          />
          <button
            className="btn primary small"
            onClick={() => {
              store.createProgram();
              onEdit();
            }}
          >
            + New
          </button>
        </div>
      </div>

      {favorites.length > 0 ? (
        <>
          <div className="section-label">Favourites</div>
          {favorites.map(card)}
        </>
      ) : null}

      {matches.length === 0 ? <div className="empty">Nothing matches.</div> : null}

      {[...CATEGORY_ORDER, 'custom' as const].map((key) => {
        const items = grouped.get(key);
        if (!items || items.length === 0) return null;
        return (
          <div key={key}>
            <div className="section-label">
              {key === 'custom' ? 'Your programs' : CATEGORY_LABELS[key]}
            </div>
            {items.map(card)}
          </div>
        );
      })}

      {notConnected ? (
        <Modal title="Not connected yet" onClose={() => setNotConnected(false)}>
          <p className="muted" style={{ marginTop: 0 }}>
            The device has to be connected before a program can be sent to it.
          </p>
          <ol className="caveat-list">
            <li>Switch the PEMF device on.</li>
            <li>Go to <strong>Home</strong> and tap <strong>Connect</strong>.</li>
            <li>Pick <strong>PEMF-Controller</strong> from the list your browser shows.</li>
          </ol>
          <p className="faint" style={{ marginBottom: 0 }}>
            You can still build and edit programs without connecting — just not run them.
          </p>
        </Modal>
      ) : null}
    </>
  );
}
